import { useEffect, useState, type JSX } from 'react';
import { api, type ActivityDetail, type CourseDetailProgress } from '../../api.ts';
import { Progress } from '../../components/Progress.tsx';

/**
 * One pupil's work, opened under their name on the roll.
 *
 * The roll answers "how far along"; this answers "at what". A teacher who sees 40% wants to know
 * whether the child is stuck on a test or simply has not opened the next module, and those are
 * different conversations to have with them.
 *
 * Loaded when the row is opened rather than with the list: this is several queries per pupil, and
 * a class of thirty would pay all of them to show a panel nobody looked at.
 */

/** What a lesson's own state is called in the register the teacher already speaks. */
const LESSON_WORDS: Record<string, string> = {
  not_started: 'не начат',
  in_progress: 'начат',
  submitted: 'на проверке',
  completed: 'пройден',
  failed: 'не сдан',
};

const KIND_WORDS: Record<ActivityDetail['kind'], string> = {
  video: 'Видео',
  assignment: 'Домашняя работа',
  quiz: 'Тест',
};

/**
 * What one piece of a lesson amounts to, in words.
 *
 * Per kind, because the same state means different things: a video at "partial" was half watched,
 * a test at "partial" was taken and failed. One shared vocabulary would be shorter to write and
 * wrong to read.
 */
function describe(activity: ActivityDetail): string {
  const percent = activity.percent;

  if (activity.kind === 'video') {
    if (activity.state === 'done') return 'просмотрено';
    if (activity.state === 'partial') return `просмотрено ${percent ?? 0}%`;
    return 'не открывал';
  }

  if (activity.kind === 'assignment') {
    if (activity.state === 'done') return percent === null ? 'принята' : `принята, ${percent}%`;
    if (activity.state === 'waiting') return 'ждёт проверки';
    if (activity.state === 'returned') return 'на доработку';
    return 'не сдана';
  }

  if (activity.state === 'done') return percent === null ? 'сдан' : `сдан, ${percent}%`;
  if (activity.state === 'partial') {
    const score = percent === null ? 'не пройден' : `не пройден, ${percent}%`;
    // Out of tries is the fact a teacher acts on, so it is said rather than implied by a button.
    return activity.attempts_left === 0 ? `${score} · попытки кончились` : score;
  }
  if (activity.state === 'waiting') return 'начат, не завершён';

  return 'не начинал';
}

export function StudentProgress({ profileId }: { profileId: string }): JSX.Element {
  const [granting, setGranting] = useState('');
  const [courses, setCourses] = useState<CourseDetailProgress[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    api.authoring
      .studentProgress(profileId)
      .then((rows) => alive && setCourses(rows))
      .catch((cause: unknown) =>
        alive
          ? setError(cause instanceof Error ? cause.message : 'Не удалось загрузить историю')
          : undefined,
      );

    return () => {
      alive = false;
    };
  }, [profileId]);

  /**
   * Gives the learner the quiz's whole allowance again.
   *
   * The full allowance rather than one more try: a teacher pressing this has decided the person
   * should have another proper go at it, and handing back a single attempt only brings them back
   * to this same screen tomorrow.
   */
  async function giveAttempts(quizId: string): Promise<void> {
    setGranting(quizId);
    setError('');

    try {
      await api.authoring.grantAttempts(quizId, profileId, 3);
      setCourses(await api.authoring.studentProgress(profileId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось вернуть попытки');
    } finally {
      setGranting('');
    }
  }

  if (error) return <p className="error">{error}</p>;
  if (courses === null) return <p className="muted">Загружаем историю…</p>;
  if (courses.length === 0) {
    return <p className="muted">Ученик пока не зачислен ни на один курс.</p>;
  }

  return (
    <div className="history">
      {courses.map((course) => (
        <div key={course.enrollment_id} className="history__course">
          <div className="history__course-head">
            <strong>{course.title}</strong>
            <Progress percent={course.progress_percent} compact />
          </div>

          {course.modules.length === 0 ? (
            <p className="muted history__empty">В курсе пока нет модулей.</p>
          ) : null}

          {course.modules.map((module) => (
            <div key={module.id} className="history__module">
              <div className="history__module-head">
                <span className={`dot${module.completed ? ' dot--done' : ''}`} aria-hidden="true" />
                <span className="history__module-title">{module.title}</span>
                <span className={`tag${module.completed ? ' tag--done' : ''}`}>
                  {module.completed ? 'пройден' : 'в работе'}
                </span>
              </div>

              {module.lessons.map((lesson) => (
                <div key={lesson.id} className="history__lesson">
                  <div className="history__lesson-head">
                    <span
                      className={`dot dot--small${lesson.state === 'completed' ? ' dot--done' : ''}`}
                      aria-hidden="true"
                    />
                    <span className="history__lesson-title">{lesson.title}</span>
                    <span className="muted history__state">
                      {LESSON_WORDS[lesson.state] ?? lesson.state}
                    </span>
                    {/*
                     * The lesson's own share, so the course figure can be checked rather than
                     * believed. A teacher seeing 30% on the roll and 60% on the pupil's screen
                     * should be able to see here that the first is these lessons averaged.
                     */}
                    <span className="history__share">{Math.round(lesson.fraction * 100)}%</span>
                  </div>

                  {lesson.activities.map((activity, index) => (
                    <div
                      // Two videos in one lesson share no id, so position completes the key.
                      key={`${activity.kind}-${index}`}
                      className={`history__item history__item--${activity.state}`}
                    >
                      <span className="history__kind">{KIND_WORDS[activity.kind]}</span>
                      <span className="history__item-title">{activity.title}</span>
                      <span className="history__verdict">{describe(activity)}</span>

                      {/*
                       * Somebody out of tries is stuck quietly — the test simply stops being
                       * offered to them — so the button to unstick them belongs here, where a
                       * teacher is already looking at why they are stuck.
                       */}
                      {activity.kind === 'quiz' &&
                      activity.ref_id &&
                      activity.attempts_left === 0 ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          disabled={granting === activity.ref_id}
                          onClick={() => void giveAttempts(activity.ref_id!)}
                        >
                          {granting === activity.ref_id ? '…' : 'Вернуть попытки'}
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
